import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Mail, Star, MessageSquarePlus } from 'lucide-react';
import { businessInfo, services, hipagesReviews, projectImages } from '../data/mockData';
import ReviewCarousel from '../components/ReviewCarousel';
import ReviewsGrid from '../components/ReviewsGrid';
import HipagesStatsCard from '../components/HipagesStatsCard';
import LeaveReviewModal from '../components/LeaveReviewModal';

const Home = () => {
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleReviewSuccess = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div 
          className="hero-background"
          style={{ backgroundImage: `url(${projectImages.find(img => img.featured)?.url || projectImages[0].url})` }}
        ></div>
        
        <div className="hero-content">
          <h1 className="hero-title">
            Premium Glass Fencing & Balustrades in Sydney
          </h1>
          <p className="hero-subtitle">
            Transform your property with stunning frameless glass installations. Quality workmanship, professional service, Australian standards compliant.
          </p>
          <div className="hero-buttons">
            <Link to="/contact" className="btn btn-primary">
              Get a Free Quote
              <ArrowRight size={20} />
            </Link>
            <a href="tel:+61414443454" className="btn btn-secondary">
              <Phone size={20} />
              Call Now
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="features-grid">
            <div className="feature-card">
              <CheckCircle className="feature-icon" />
              <h3>Licensed & Insured</h3>
              <p>Fully licensed professionals with comprehensive insurance coverage</p>
            </div>
            <div className="feature-card">
              <CheckCircle className="feature-icon" />
              <h3>Quality Materials</h3>
              <p>Premium toughened glass and stainless steel hardware</p>
            </div>
            <div className="feature-card">
              <CheckCircle className="feature-icon" />
              <h3>Expert Installation</h3>
              <p>Years of experience delivering flawless installations</p>
            </div>
            <div className="feature-card">
              <CheckCircle className="feature-icon" />
              <h3>Australian Standards</h3>
              <p>All installations meet AS 1926.1-2012 requirements</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="services-overview">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Services</h2>
            <p className="section-subtitle">
              Comprehensive glass solutions for residential and commercial properties
            </p>
          </div>

          <div className="services-grid">
            {services.map((service) => (
              <div key={service.id} className="service-card">
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <ul className="service-features">
                  {service.features.map((feature, index) => (
                    <li key={index}>
                      <CheckCircle size={16} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="section-cta">
            <Link to="/services" className="btn btn-primary">
              View All Services
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="gallery-preview">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Recent Projects</h2>
            <p className="section-subtitle">
              See the quality of our work across Sydney
            </p>
          </div>

          <div className="gallery-grid">
            {projectImages.map((image) => (
              <div key={image.id} className="gallery-item">
                <img src={image.url} alt={image.title} />
                <div className="gallery-overlay">
                  <h4>{image.title}</h4>
                  <p>{image.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="section-cta">
            <Link to="/gallery" className="btn btn-primary">
              View Full Gallery
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews Section - Hipages Verified */}
      <section className="reviews-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Client Reviews</h2>
            <p className="section-subtitle">
              Real feedback from our valued customers
            </p>
          </div>

          <div className="reviews-layout">
            <div className="reviews-main">
              <div className="reviews-carousel-section">
                <h3 className="subsection-title">Featured Reviews</h3>
                <ReviewCarousel reviews={hipagesReviews} />
              </div>

              <div className="reviews-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => setIsReviewModalOpen(true)}
                >
                  <MessageSquarePlus size={20} />
                  Leave a Recommendation
                </button>
              </div>

              <div className="reviews-grid-section">
                <h3 className="subsection-title">All Reviews</h3>
                <ReviewsGrid key={refreshKey} initialReviews={hipagesReviews} />
              </div>
            </div>

            <div className="reviews-sidebar">
              <HipagesStatsCard />
            </div>
          </div>
        </div>
      </section>

      <LeaveReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        onSuccess={handleReviewSuccess}
      />

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Transform Your Property?</h2>
            <p className="cta-subtitle">
              Get a free, no-obligation quote for your glass fencing or balustrade project
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-large btn-primary">
                Request a Quote
                <ArrowRight size={22} />
              </Link>
              <a href="tel:+61414443454" className="btn btn-large btn-secondary">
                <Phone size={22} />
                {businessInfo.phone}
              </a>
            </div>
            <p className="cta-hours">
              <span className="hours-label">Working Hours:</span> {businessInfo.hours.weekdays}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
